import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Close from "../../../assets/close-circle.svg";
import ReturnCancelSelector from "../../../components/RejectionSelector";
import Dropdown from "../../../components/Dropdown";
import { toast } from "react-toastify";
import {
  cancelDelivery,
  addDeliveryNote,
  reassignDelivery,
  rescheduleDelivery,
  getDeliveryNotes,
  getCancellationReasonsEnum, // Import the new function
} from "../../../api/delivery-driver";

interface TimelinePopupProps {
  onClose: () => void;
  deliveryNumber: any;
  reasonofcancellation: any;
  driverId?: any;
  onOrderStatusUpdate: (orderId: string, updateObj: any) => void; // Add this line
}

const TimelinePopup: React.FC<TimelinePopupProps> = ({
  onClose,
  deliveryNumber,
  reasonofcancellation,
  driverId,
  onOrderStatusUpdate, // Add this line
}) => {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState("");
  const [selectedOption, setSelectedOption] = useState("Return Delivery to Driver");
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [previousNotes, setPreviousNotes] = useState<any>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [reasons, setReasons] = useState<any[]>([]); // State for reasons
  const [selectedDate, setSelectedDate] = useState<string>("");

  // Fetch cancellation reasons when the popup opens
  useEffect(() => {
    const fetchReasons = async () => {
      try {
        const res = await getCancellationReasonsEnum('en');
        if (res.data && res.data.cancelled_delivery_reasons) {
          setReasons(res.data.cancelled_delivery_reasons);
        }
      } catch (error) {
        console.error('Failed to fetch cancellation reasons', error);
      }
    };
    fetchReasons();
  }, []);

  useEffect(() => {
    const fetchPreviousNotes = async () => {
      setNotesLoading(true);
      try {
        const notes = await getDeliveryNotes(deliveryNumber?.order_id);
        setPreviousNotes(notes.data.notes.map((noteObj: any) => noteObj.note));
      } catch (error) {
        console.error("Failed to fetch previous notes", error);
      } finally {
        setNotesLoading(false);
      }
    };
    fetchPreviousNotes();
  }, [deliveryNumber]);

  // Set default date to today when 'Reschedule Order' is selected
  useEffect(() => {
    if (selectedOption === "Reschedule Order") {
      const today = new Date().toISOString().split("T")[0];
      setSelectedDate(today);
    }
  }, [selectedOption]);

  const handleFeedbackChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFeedback(event.target.value);
  };

  const handleReasonChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedReason(event.target.value);
  };

  const handleFeedbackSubmit = async () => {
    setLoading(true);
    setErrorMessage("");

    const reasonToSend = selectedOption === "Cancel Order" ? selectedReason : undefined;

    try {
      if (selectedOption === "Cancel Order") {
        if (!reasonToSend) {
          setErrorMessage("Please select a reason for cancellation.");
          setLoading(false);
          return;
        }
        await cancelDelivery(deliveryNumber?.order_id, { reason: reasonToSend });
        onOrderStatusUpdate(deliveryNumber?.order_id, { cancel_order: true }); // Update parent
        toast.success("Order cancelled successfully.");
      }

      if (selectedOption === "Return Delivery to Driver") {
        await reassignDelivery(deliveryNumber?.order_id, driverId);
        onOrderStatusUpdate(deliveryNumber?.order_id, { return_delivery_to_driver: true }); // Update parent
        toast.success("Delivery reassigned to driver successfully.");
      }
      if (selectedOption === "Reschedule Order") {
        if (!selectedDate) {
          setErrorMessage("Please select a date for rescheduling.");
          setLoading(false);
          return;
        }
        await rescheduleDelivery(deliveryNumber?.order_id, driverId, selectedDate);
        onOrderStatusUpdate(deliveryNumber?.order_id, { rescheduled: true }); // Update parent
        toast.success("Order rescheduled successfully.");
      }

      await addDeliveryNote(deliveryNumber?.order_id, feedback, {});
      setFeedback("");
      setSelectedDate("");
      // Delay closing the popup to allow the Snackbar to show
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (error: any) {
      console.error('Error response:', error.response?.data?.detail?.message);
      if (error.response?.data?.detail?.message) {
        setErrorMessage(error.response.data.detail.message);
      } else {
        if (selectedOption === "Reschedule Order") {
          setErrorMessage("An unexpected error occurred while rescheduling the order.");
        } else if (selectedOption === "Return Delivery to Driver") {
          setErrorMessage("An unexpected error occurred while reassigning the delivery.");
        } else if (selectedOption === "Cancel Order") {
          setErrorMessage("An unexpected error occurred while cancelling the order.");
        } else {
          setErrorMessage("An error occurred while processing your request.");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto relative">
        <div className="flex justify-between items-center pb-6 border-b">
          <h2 className="text-2xl font-bold">{t("rejectionReport.feedback")}</h2>
          <button className="text-3xl font-bold" onClick={onClose}>
            <img src={Close} alt="Close" />
          </button>
        </div>

        <ReturnCancelSelector
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
        />

        {selectedOption === "Cancel Order" && (
          <div className="my-4">
            <Dropdown
              label={t("rejectionReport.reasonForCancellation")}
              options={reasons}
              selectedValue={selectedReason}
              onChange={(val) => setSelectedReason(String(val))}
              placeholder={t("rejectionReport.selectReason")}
              searchable={true}
            />
          </div>
        )}

        {selectedOption === "Reschedule Order" && (
          <div className="my-4">
            <label className="block mb-2 font-medium">{t("rejectionReport.selectRescheduleDate")}</label>
            <input
              type="date"
              className="border rounded px-3 py-2 w-full"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              max={(() => {
                const today = new Date();
                const maxDate = new Date(today);
                maxDate.setDate(today.getDate() + 7);
                return maxDate.toISOString().split("T")[0];
              })()}
            />
          </div>
        )}

        {errorMessage && (
          <div className="text-red-600 bg-red-100 border border-red-300 rounded p-2 my-4">
            {errorMessage}
          </div>
        )}

        <div className="flex justify-end">
          <button
            className="bg-purple text-white px-4 py-2 mt-4 rounded-md hover:bg-purple-300 disabled:bg-purple-300"
            onClick={handleFeedbackSubmit}
          >
            {loading ? t("rejectionReport.submitting") : t("rejectionReport.submit")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimelinePopup;
